import { ColDef, GridOptions, GridReadyEvent } from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
} from '@helios/ag-grid-config'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { splitNumbers } from '@helios/utils'
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { AgGridApi } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { RulesKey } from '../../panel-key'
import { FindRulesForm, FindRulesRequest } from '../models/find-rules.model'
import { Rules } from '../models/rules.model'
import { RulesService } from '../services/rules-service/rules.service'

@Component({
  selector: 'hs-rules-grid',
  templateUrl: './rules-grid.component.html',
  styleUrls: ['./rules-grid.component.scss'],
})
export class RulesGridComponent implements OnInit {
  constructor(
    public rulesService: RulesService,
    public toastService: ToastService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly preferences: UserPreferencesService,
  ) {}

  @ViewChild('searchRulePopoverComponent')
  searchRulePopoverComponent?: NgbPopover

  @Output()
  ruleIdSelected$ = new EventEmitter<number>()

  @Output() isSearchPopoverShownChange = new EventEmitter<boolean>()
  @Output() resetPopOverFilter = new EventEmitter<void>()

  isRuleLoading = false
  clearFiltersEnabled = false
  filterCount = 0
  formInput?: FindRulesForm
  onSearchPopoverShown() {
    this.searchRulePopoverComponent?.open()
  }

  onSearchPopoverHidden() {
    this.searchRulePopoverComponent?.close()
  }

  gridApi!: AgGridApi
  rules: Rules[] = []
  rowRulesData: Rules[] = []
  rulesField = composeFieldPath(this.rules)
  private readonly destroySubject$ = new Subject<void>()
  _accounts: number[] = []

  @Input()
  set searchInput(input: FindRulesForm | undefined) {
    if (input) {
      this.formInput = input
      this.searchRulesfromParams(input)
      this.updateFilterCountAndEnableButton(input.rootIds.length)
      this.searchRulePopoverComponent?.close()
    }
  }

  @Input() set focusedIds(accountIds: number[] | null) {
    if (accountIds && (accountIds?.length ?? -1) > 0) {
      this._accounts = accountIds
      const rulesRequest = { accountIds: accountIds } as FindRulesRequest
      this.fetchFilteredRules(rulesRequest)
      this.updateFilterCountAndEnableButton(1)
      this.searchRulePopoverComponent?.close()
    }
  }

  ngOnInit(): void {
    if (!this.formInput && this._accounts.length === 0) {
      this.fetchAllRules()
    }
  }

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.api?.destroy()
  }

  searchRulesfromParams(input: FindRulesForm) {
    const rulesRequest = {} as FindRulesRequest
    if (input.rootIdType) {
      const rootIds = splitNumbers(input.rootIds)
      switch (input.rootIdType) {
        case 'account':
          rulesRequest.accountIds = rootIds
          break
        case 'client':
          rulesRequest.clientIds = rootIds
          break
        case 'ultimateParent':
          rulesRequest.parentClientIds = rootIds
          break
        case 'datafeed':
          rulesRequest.datafeedIds = rootIds
          break
      }
    }
    this.fetchFilteredRules(rulesRequest)
  }

  fetchFilteredRules(rulesRequest: FindRulesRequest) {
    this.isRuleLoading = true
    this.ruleIdSelected$.emit(undefined)
    this.rulesService
      .getFilteredRules(rulesRequest)
      .pipe(take(1))
      .subscribe(
        rules => {
          this.loadGridRules(rules)
          this.isRuleLoading = false
        },
        () => {
          this.isRuleLoading = false
          this.rowRulesData = []
        },
      )
  }

  fetchAllRules() {
    this.isRuleLoading = true
    this.ruleIdSelected$.emit(undefined)
    this.rulesService
      .getRules()
      .pipe(take(1))
      .subscribe(
        rules => {
          this.loadGridRules(rules)
          this.isRuleLoading = false
        },
        () => {
          this.isRuleLoading = false
          this.rowRulesData = []
        },
      )
  }

  loadGridRules(rules: Rules[]) {
    const rulesData: Rules[] = []
    rules.forEach((action: Rules) => {
      rulesData.push({
        ruleType: action.ruleType,
        ruleDescription: action.ruleDescription,
        ruleId: action.ruleId,
        ruleDirectory: action.ruleDirectory,
        ruleName: action.ruleName,
        requester: action.requester,
        author: action.author,
        datafeedIds: action.datafeedIds,
        approver: action.approver,
        lastModifiedBy: action.lastModifiedBy,
        case: action.case,
        parentClientIds: action.parentClientIds,
        clientIds: action.clientIds,
        accountIds: action.accountIds,
        dataSourceTypes: action.dataSourceTypes,
      })
    })
    this.rowRulesData = rulesData
  }

  gridOptions: GridOptions = {
    defaultColDef: {
      filterParams: {
        buttons: ['apply', 'reset'],
        closeOnApply: true,
        defaultToNothingSelected: true,
      },
      sortable: true,
      enableRowGroup: true,
    },
    enableBrowserTooltips: true,
    rowSelection: 'single',
    suppressRowClickSelection: false,
    columnDefs: this.createColumnDefs(),
    groupDefaultExpanded: 1,
    onSelectionChanged: event => {
      const rule = event.api.getSelectedNodes().map(x => x.data as Rules)
      this.ruleIdSelected$.emit(rule[0].ruleId)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFirstDataRendered: (params: any) => {
      params.api.sizeColumnsToFit()
    },
    groupDisplayType: 'groupRows',
    isGroupOpenByDefault: () => true,

    onGridReady: params => this.onGridReady(params),
  }

  /** @internal */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params
    this.savedViewService.manageSavedView(
      params,
      RulesKey,
      this.destroySubject$,
    )
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )

    enableHeliosCopyBehavior(this.gridApi.api)
  }

  private createColumnDefs(): ColDef[] {
    const colDef = [
      {
        field: this.rulesField('ruleType'),
        filter: 'agGroupColumnFilter',
        rowGroup: true,
      },
      {
        field: this.rulesField('ruleDirectory'),
        filter: 'agGroupColumnFilter',
        rowGroup: true,
      },
      {
        field: this.rulesField('ruleId'),
        headerName: 'Rule ID',
      },

      {
        field: this.rulesField('ruleName'),
      },
      {
        field: this.rulesField('ruleDescription'),
        width: 250,
      },
      {
        field: this.rulesField('author'),
      },
      {
        field: this.rulesField('requester'),
      },
      {
        field: this.rulesField('approver'),
      },
      {
        field: this.rulesField('lastModifiedBy'),
      },
      {
        field: this.rulesField('case'),
      },
      {
        field: this.rulesField('parentClientIds'),
        headerName: 'Parent Client IDs',
      },
      {
        field: this.rulesField('clientIds'),
        headerName: 'Client IDs',
      },
      {
        field: this.rulesField('accountIds'),
        headerName: 'Account IDs',
      },
      {
        field: this.rulesField('datafeedIds'),
        headerName: 'Data Feed IDs',
        width: 200,
      },
      {
        field: this.rulesField('dataSourceTypes'),
      },
    ]
    return colDef
  }

  clearFilters() {
    this.resetPopOverFilter.emit()
    this.filterCount = 0
    this.clearFiltersEnabled = false
    this.fetchAllRules()
  }

  updateFilterCountAndEnableButton(count: number) {
    const popoverFilterCount = Number(count !== 0)
    this.clearFiltersEnabled = popoverFilterCount > 0 ? true : false
    this.filterCount = popoverFilterCount > 0 ? 1 : 0
  }
}
